import React, { useState, useEffect } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatMoney } from '../utils/currency';
import { exportToCsv } from '../utils/exportCsv';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';
import PromptDialog from '../components/common/PromptDialog';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [currencyCode, setCurrencyCode] = useState('QAR');
  const [loading, setLoading] = useState(true);
  const [refundingId, setRefundingId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [refundTarget, setRefundTarget] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async (page = 1) => {
    try {
      const response = await api.get(`/admin/payments?page=${page}&limit=10`);
      setPayments(response.data.data);
      setPagination(response.data.pagination);
      setCurrencyCode(String(response.data?.currencyCode || 'QAR').toUpperCase());
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const openRefundDialog = (payment) => {
    if (payment.paymentMethod !== 'card') { toast.error('Only card payments can be refunded'); return; }
    if (payment.status !== 'completed') { toast.error('Only completed payments can be refunded'); return; }
    setRefundTarget(payment);
  };

  const handleRefundConfirm = async ({ refundAmount }) => {
    const payment = refundTarget;
    if (!payment?._id) return;
    const amount = refundAmount && String(refundAmount).trim() !== '' ? Number(refundAmount) : undefined;
    if (amount != null && (!Number.isFinite(amount) || amount <= 0)) {
      toast.error('Invalid refund amount');
      return;
    }
    try {
      setRefundingId(payment._id);
      setRefundTarget(null);
      await api.post(`/admin/payments/${payment._id}/refund`, amount ? { amount } : {});
      toast.success('Refund processed');
      await fetchPayments(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Refund failed');
    } finally {
      setRefundingId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Payments</h1>
        <div className="flex items-center gap-3">
          <span className="text-gray-500">{pagination.total} total payments</span>
          <button
            className="btn-secondary flex items-center gap-1"
            onClick={() => exportToCsv(
              '/admin/exports/payments',
              {},
              'payments.csv'
            )}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Payment ID</th>
                <th className="table-header">User</th>
                <th className="table-header">Driver</th>
                <th className="table-header">Amount</th>
                <th className="table-header">Method</th>
                <th className="table-header">Status</th>
                <th className="table-header">Date</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment._id} className="hover:bg-gray-50">
                  <td className="table-cell">
                    <span className="font-mono text-sm">#{payment._id.slice(-8)}</span>
                  </td>
                  <td className="table-cell">
                    {payment.user ? `${payment.user.firstName} ${payment.user.lastName}` : 'N/A'}
                  </td>
                  <td className="table-cell">
                    {payment.driver ? `${payment.driver.firstName} ${payment.driver.lastName}` : 'N/A'}
                  </td>
                  <td className="table-cell font-medium">
                    {formatMoney(payment.amount || 0, payment.currency || currencyCode || 'QAR')}
                  </td>
                  <td className="table-cell capitalize">{payment.paymentMethod}</td>
                  <td className="table-cell"><StatusBadge status={payment.status} /></td>
                  <td className="table-cell text-sm text-gray-500">
                    {new Date(payment.createdAt).toLocaleString()}
                  </td>
                  <td className="table-cell">
                    {payment.paymentMethod === 'card' && payment.status === 'completed' ? (
                      <button
                        onClick={() => openRefundDialog(payment)}
                        disabled={refundingId === payment._id}
                        className="btn-secondary disabled:opacity-50"
                      >
                        {refundingId === payment._id ? 'Refunding...' : 'Refund'}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          pages={pagination.pages}
          onPageChange={(p) => fetchPayments(p)}
        />
      </div>

      <PromptDialog
        isOpen={!!refundTarget}
        title="Refund Payment"
        fields={[{ name: 'refundAmount', label: `Refund amount (blank = full refund of ${refundTarget ? formatMoney(refundTarget.amount, refundTarget.currency || currencyCode) : ''})`, type: 'text', placeholder: '' }]}
        confirmLabel="Process Refund"
        loading={!!refundingId}
        onConfirm={handleRefundConfirm}
        onCancel={() => setRefundTarget(null)}
      />
    </div>
  );
};

export default Payments;
